import cloneDeep from "lodash/cloneDeep";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { KEY_OBJECT, KeyObj } from "../types/keys/key-object";
import { ArrayKeyOf, StringDict, TypeKeyOf } from "../types/keys/type-key-of";
import { shouldValidate } from "./useShouldValidate";
import { useValidateArray, useValidateObject } from "./validate-yup";

export interface MultiFieldSet<T extends Record<string, any>> {
  values: T[];
  onChange: <Type>(
    index: number,
    value: Type,
    field: TypeKeyOf<T, Type>,
  ) => void;
  removeItem: (index: number) => void;
  addItem: (v: T) => void;
  degreeTypeRequired?: boolean;
  universityRequired?: boolean;
  endDateRequired?: boolean;
  clear: () => void;
  reset: () => void;
  set: (values: T[]) => void;
  setValue: (index: number, field: keyof T, value: any) => void;
  setItem: (index: number, value: T) => void;
  validation: KeyObj<T, boolean>[];
  validationMsg: Partial<StringDict<T>>[];
  notEnoughItems?: boolean;
  isInvalid: KeyObj<T, boolean>[];
  hasAllValid: boolean;
  makeAllValidate: () => void;
  onBlur: (fieldKey: keyof T) => () => void;
  isDirty: KeyObj<T, boolean>[];
  hasAnyDirty: boolean;
}

export const useMultiFieldSet = <T extends Record<string, any>>(
  initialValue: T[],
  deps?: any[],
  yupSchema?: Yup.SchemaOf<T>,
  opts?: {
    minLength?: number;
  },
): MultiFieldSet<T> => {
  const [fieldSetIsDirty, setFieldSetIsDirty] = useState<boolean>(false);
  const [values, setValues] = useState<T[]>(cloneDeep(initialValue));
  const [isDirty, setIsDirty] = useState<KeyObj<T, boolean>[]>(
    initialValue.map((v) => KEY_OBJECT(v, false)),
  );
  const [blurred, setBlurred] = useState<KeyObj<T, boolean>[]>(
    initialValue.map((v) => KEY_OBJECT(v, false)),
  );

  useEffect(() => {
    if (deps != null) {
      setValues(cloneDeep(initialValue));
    }
  }, deps);

  const addItem = (item: T) => {
    setValues((v) => [...v, item]);
  };

  const removeItem = (index: number) => {
    setValues((v) => v.filter((_, i) => i !== index));
  };

  const setValue = <Type>(
    index: number,
    name: TypeKeyOf<T, Type>,
    value: Type,
  ) => {
    setIsDirty((dirtyState) => {
      return dirtyState.map((item, i) => {
        if (i === index) {
          return { ...item, [name]: true };
        }
        return item;
      });
    });
    setValues((v) => {
      return v.map((item, i) => {
        if (i === index) {
          return { ...item, [name]: value };
        }
        return item;
      });
    });
  };

  const onChange = <Type>(
    index: number,
    value: Type,
    name: TypeKeyOf<T, Type>,
  ) => {
    setIsDirty((dirtyState) => {
      return dirtyState.map((item, i) => {
        if (i === index) {
          return { ...item, [name]: true };
        }
        return item;
      });
    });

    setValues((v) => {
      return v.map((item, i) => {
        if (i === index) {
          return { ...item, [name]: value };
        }
        return item;
      });
    });
  };

  const reset = () => {
    setValues(initialValue);
  };

  const set = (value: T[]) => {
    setValues((s) => value);
  };

  const clear = () => {
    setValues([]);
  };

  const makeAllValidate = () => {
    setIsDirty(() => {
      return values.map((item) => {
        return KEY_OBJECT(item, true);
      });
    });
    setBlurred(() => {
      return values.map((item) => {
        return KEY_OBJECT(item, true);
      });
    });
    // mark the entire fieldset as dirty for cases where we have no values but require at least one
    setFieldSetIsDirty(true);
  };

  const setItem = (index: number, value: T) => {
    setIsDirty(() => {
      return values.map((item, i) => {
        if (i === index) {
          return KEY_OBJECT(value, true);
        }
        return item;
      });
    });

    setValues((v) => {
      return v.map((item, i) => {
        if (i === index) {
          return value;
        }
        return item;
      });
    });
  };

  const onBlur = (fieldKey: keyof T) => () => {
    setBlurred(() => {
      return values.map((item) => {
        return { ...item, [fieldKey]: true };
      });
    });
  };

  const { validationMsg, isInvalid, hasAllValid } = useValidateArray(
    values,
    yupSchema,
  );

  const validation = useMemo(() => {
    return isInvalid.map((invalid, i) => {
      let validation: Partial<KeyObj<T, boolean>> = {};
      let fieldKey: keyof T;
      for (fieldKey in invalid) {
        validation[fieldKey] =
          invalid[fieldKey] &&
          shouldValidate(isDirty?.[i]?.[fieldKey], blurred?.[i]?.[fieldKey]);
      }

      return validation as KeyObj<T, boolean>;
    });
  }, [isInvalid, isDirty, blurred]);

  const hasAnyDirty: boolean = useMemo(() => {
    for (let [_k, v] of Object.entries(isDirty)) {
      if (v) return true;
    }

    return fieldSetIsDirty;
  }, [isDirty, fieldSetIsDirty]);

  const notEnoughItems = opts?.minLength
    ? values.length < opts.minLength
    : false;

  return {
    values: values,
    addItem,
    removeItem,
    onChange,
    clear,
    reset,
    set,
    setValue,
    validationMsg,
    isInvalid,
    makeAllValidate,
    hasAllValid,
    setItem,
    onBlur,
    isDirty,
    hasAnyDirty,
    notEnoughItems,
    validation,
  };
};

/**
 * A construct for handling object state, particularly (but not limited to) for use with form validation. Most of the fields of this object are either:
 *
 * - Objects whose keys correspond to the keys of the passed-in state, used for accessing information about the state (e.g. the current state, whether a field has been changed, or whether a field is valid);
 * - Functions whose first argument takes in a key of the passed-in state, used for modifying the state in some way (e.g. a change handler, appending an item to an array field, or toggling an item in a multi-select).
 */
export interface FieldSet<StateObj extends { [key: string]: any }> {
  values: Readonly<StateObj>;

  /**
   * @deprecated use `curriedOnChange` or `setValue` instead.
   */
  onChange: <Type>(value: Type, name: TypeKeyOf<StateObj, Type>) => void;

  /**
   * @param name a key of the state object.
   * @returns a change handler, whose input type matches the value in the state object corresponding to the given key.
   */
  curriedOnChange: <Key extends keyof StateObj>(
    name: Key,
  ) => (value: StateObj[Key]) => void;

  /**
   * Set all values to `initialValues`, mark all fields as not dirty, and mark all fields as not blurred.
   */
  reset: () => void;

  resetIsDirty: () => void;

  /**
   * Set (a part of) the state object directly. Marks any affected fields as dirty.
   */
  set: (v: Partial<StateObj>) => void;

  /**
   * Set a specific field of the state object directly. Will mark the affected field as dirty.
   */
  setValue: <Key extends keyof StateObj>(
    name: Key,
    value: StateObj[Key],
  ) => void;

  /**
   * Event handler for blur events, which will mark the corresponding key as blurred.
   */
  onBlur: (fieldKey: keyof StateObj) => () => void;

  isInvalid: KeyObj<StateObj, boolean>;

  /**
   * `true` if the field is invalid and should be displayed as such to the user (e.g. the field is invalid, is dirty and has been blurred by the user); `false` otherwise.
   */
  validation: KeyObj<StateObj, boolean>;

  /**
   * `undefined` if the field is valid; `string` describing the validation error otherwise.
   */
  validationMsg: Partial<KeyObj<StateObj, string>>;

  hasAllValid: boolean;

  blurred: KeyObj<StateObj, boolean>;

  isDirty: KeyObj<StateObj, boolean>;

  hasAnyDirty: boolean;

  /**
   * Force validation by marking all fields as dirty and blurred.
   */
  makeAllValidate: () => void;

  appendItem: <Key extends ArrayKeyOf<StateObj>>(
    fieldKey: Key,
    item: StateObj[Key][number],
  ) => void;

  prependItem: <Key extends ArrayKeyOf<StateObj>>(
    fieldKey: Key,
    item: StateObj[Key][number],
  ) => void;

  /**
   * Remove item at a specified index.
   */
  removeItem: (fieldKey: ArrayKeyOf<StateObj>, index: number) => void;

  /**
   * Set value of the item at a specified index.
   */
  setItemValue: <Key extends ArrayKeyOf<StateObj>>(
    fieldKey: Key,
    index: number,
    newItem: StateObj[Key][number],
  ) => void;

  /**
   * Move item from one index in the array to another.
   */
  moveItem: (
    fieldKey: ArrayKeyOf<StateObj>,
    startIndex: number,
    endIndex: number,
  ) => void;
}

/**
 * A hook for handling object state, particularly (but not limited to) for use with form validation.
 *
 * @param initialValues the initial state.
 * @param deps an array of dependencies such that, when it changes, the current state will be reset to the value of `initialValues`.
 * @param yupSchema a `yup` schema against which the current state will be validated.
 * @returns a `FieldSet` object with many helper methods for reading from and writing to the state.
 */
export function useFieldSet<StateObj extends Record<string, any>>(
  initialValues: StateObj,
  deps?: unknown[],
  yupSchema?: Yup.SchemaOf<StateObj>,
  opts?: {
    shouldNotUpdateInitialValues?: boolean;
  },
): FieldSet<StateObj> {
  const [values, setValues] = useState(cloneDeep(initialValues));

  const [isDirty, setIsDirty] = useState(KEY_OBJECT(initialValues, false));

  const [blurred, setBlurred] = useState(KEY_OBJECT(initialValues, false));

  useEffect(() => {
    if (deps && !opts?.shouldNotUpdateInitialValues) {
      setValues(cloneDeep(initialValues));
    }
  }, deps);

  const onChange = <Type>(value: Type, name: TypeKeyOf<StateObj, Type>) => {
    setIsDirty((dirtyState) => ({
      ...dirtyState,
      [name]: true,
    }));

    setValues((state) => ({
      ...state,
      [name]: value,
    }));
  };

  const curriedOnChange =
    <Key extends keyof StateObj>(name: Key) =>
    (value: StateObj[Key]) =>
      setValue(name, value);

  const setValue = useCallback(
    <Key extends keyof StateObj>(name: Key, value: StateObj[Key]) => {
      setIsDirty((dirtyState) => ({
        ...dirtyState,
        [name]: true,
      }));

      setValues((state) => ({
        ...state,
        [name]: value,
      }));
    },
    [],
  );

  const reset = () => {
    setValues(initialValues);

    resetIsDirty();

    setBlurred(KEY_OBJECT(initialValues, false));
  };

  const resetIsDirty = () => {
    setIsDirty(KEY_OBJECT(initialValues, false));
  };

  const set = (v: Partial<StateObj>) => {
    setIsDirty({
      ...isDirty,
      ...KEY_OBJECT(v, true),
    });

    setValues((s) => {
      return {
        ...s,
        ...v,
      };
    });
  };

  const onBlur = (fieldKey: keyof StateObj) => () => {
    setBlurred((blurredState) => ({
      ...blurredState,
      [fieldKey]: true,
    }));
  };

  const { validationMsg, isInvalid } = useValidateObject(values, yupSchema);

  const validation = useMemo(() => {
    let validation: Partial<KeyObj<StateObj, boolean>> = {};
    let fieldKey: keyof StateObj;
    for (fieldKey in isInvalid) {
      validation[fieldKey] =
        isInvalid[fieldKey] &&
        shouldValidate(isDirty[fieldKey], blurred[fieldKey]);
    }

    return validation as KeyObj<StateObj, boolean>;
  }, [isInvalid, isDirty, blurred]);

  const hasAllValid: boolean = useMemo(() => {
    let stateKey: keyof StateObj;
    for (stateKey in values) {
      if (isInvalid[stateKey]) return false;
    }

    return true;
  }, [values, isInvalid]);

  const makeAllValidate = () => {
    setIsDirty(KEY_OBJECT(initialValues, true));
    setBlurred(KEY_OBJECT(initialValues, true));
  };

  const hasAnyDirty: boolean = useMemo(() => {
    for (let [_k, v] of Object.entries(isDirty)) {
      if (v) return true;
    }

    return false;
  }, [isDirty]);

  // Manipulating array fields
  // #region

  const appendItem = useCallback(
    <Key extends ArrayKeyOf<StateObj>>(
      fieldKey: Key,
      item: StateObj[Key][number],
    ) => {
      setIsDirty((dirtyState) => ({
        ...dirtyState,
        [fieldKey]: true,
      }));

      setValues((state) => ({
        ...state,
        [fieldKey]: [...state[fieldKey], item],
      }));
    },
    [],
  );

  const prependItem = useCallback(
    <Key extends ArrayKeyOf<StateObj>>(
      fieldKey: Key,
      item: StateObj[Key][number],
    ) => {
      setIsDirty((dirtyState) => ({
        ...dirtyState,
        [fieldKey]: true,
      }));

      setValues((state) => ({
        ...state,
        [fieldKey]: [item, ...state[fieldKey]],
      }));
    },
    [],
  );

  const removeItem = useCallback(
    (fieldKey: ArrayKeyOf<StateObj>, index: number) => {
      setIsDirty((dirtyState) => ({
        ...dirtyState,
        [fieldKey]: true,
      }));

      setValues((state) => ({
        ...state,
        [fieldKey]: state[fieldKey].filter((_, idx) => idx !== index),
      }));
    },
    [],
  );

  const setItemValue = useCallback(
    <Key extends ArrayKeyOf<StateObj>>(
      fieldKey: Key,
      index: number,
      newItem: StateObj[Key][number],
    ) => {
      setIsDirty((dirtyState) => ({
        ...dirtyState,
        [fieldKey]: true,
      }));

      setValues((state) => ({
        ...state,
        [fieldKey]: state[fieldKey].map((item, idx) => {
          if (idx === index) return newItem;
          else return item;
        }),
      }));
    },
    [],
  );

  const moveItem = useCallback(
    (fieldKey: ArrayKeyOf<StateObj>, startIndex: number, endIndex: number) => {
      let newFieldValue = cloneDeep(values[fieldKey]);
      const [removed] = newFieldValue.splice(startIndex, 1);
      newFieldValue.splice(endIndex, 0, removed);

      setValue(fieldKey, newFieldValue);
    },
    [values, setValue],
  );

  // #endregion

  return {
    values,
    onChange,
    curriedOnChange,
    reset,
    resetIsDirty,
    set,
    setValue,
    onBlur,

    isInvalid,
    validation,
    validationMsg,
    hasAllValid,

    blurred,
    isDirty,
    hasAnyDirty,
    makeAllValidate,

    appendItem,
    prependItem,
    removeItem,
    setItemValue,
    moveItem,
  };
}
